import * as React from 'react';
import { DatePicker, DateRangePicker, Picker, Item, Flex, Text } from '@adobe/react-spectrum';
import { parseDate } from '@internationalized/date';
import type { DateValue } from '@internationalized/date';
import type { RangeValue } from '@react-types/shared';
import { IFilterControl, IFilterItem } from '../../FilterSetupPlaceholder';
import { OperatorSelector, IOperator } from '../OperatorSelector';
import { useFilterGuard } from '../../../../../../../hooks/useFilterGuard';
import styles from './DateFilterControl.module.css';

export interface IDateFilterControlProps {
  filter: IFilterControl;
  onFilterChange: (filter: IFilterControl) => void;
}

const DATE_OPERATORS: IOperator[] = [
  { key: 'equals', label: 'Equals' },
  { key: 'less', label: 'Before' },
  { key: 'lessOrEquals', label: 'Before or Equals' },
  { key: 'greater', label: 'After' },
  { key: 'greaterOrEquals', label: 'After or Equals' },
  { key: 'between', label: 'Between' },
  { key: 'relative_preset', label: 'Relative Date Presets' },
  { key: 'is_empty', label: 'Is Empty' },
  { key: 'is_not_empty', label: 'Is Not Empty' },
];

const RELATIVE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'last_7_days', label: 'Last 7 Days' },
  { id: 'next_7_days', label: 'Next 7 Days' },
  { id: 'last_30_days', label: 'Last 30 Days' },
  { id: 'next_30_days', label: 'Next 30 Days' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'this_month', label: 'This Month' },
  { id: 'next_month', label: 'Next Month' },
  { id: 'last_quarter', label: 'Last Quarter' },
  { id: 'this_quarter', label: 'This Quarter' },
  { id: 'next_quarter', label: 'Next Quarter' },
  { id: 'last_year', label: 'Last Year' },
  { id: 'this_year', label: 'This Year' },
  { id: 'next_year', label: 'Next Year' },
];

export const DateFilterControl = React.forwardRef<HTMLDivElement, IDateFilterControlProps>(
  (props, forwardedRef) => {
    const { filter, onFilterChange } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    // Track if we're updating operator internally to prevent useEffect from resetting it
    const isInternalOperatorUpdateRef = React.useRef<boolean>(false);
    const lastSyncedFilterInfoRef = React.useRef<string>(
        JSON.stringify(filter.filterInfo)
    );
    const currentFilterItem = filter.filterInfo[0] || {
      name: 'value',
      type: 'equals',
      value: '',
    };

    const [operator, setOperator] = React.useState<string>(currentFilterItem.type || 'equals');
    const [dateValue, setDateValue] = React.useState<DateValue | null>(null);
    const [dateRange, setDateRange] = React.useState<RangeValue<DateValue> | null>(null);
    const [relativePreset, setRelativePreset] = React.useState<string>('');

    React.useEffect(() => {
      syncGuard(filter);
    }, [filter, syncGuard]);

    React.useEffect(() => {
      // Skip if this is an internal update we just triggered
      if (isInternalOperatorUpdateRef.current) {
        isInternalOperatorUpdateRef.current = false;
        return;
      }

      const currentItem = filter.filterInfo[0];

      if (currentItem) {
        const currentFilterInfoStr = JSON.stringify(filter.filterInfo);
        
        // Only update if filter.filterInfo actually changed from parent
        if (currentFilterInfoStr !== lastSyncedFilterInfoRef.current) {
          // Determine the operator type (handle 'contains' -> 'equals' conversion)
          let operatorType = currentItem.type || 'equals';
          if (operatorType === 'contains') {
            operatorType = 'equals';
          }
          
          // Update operator state
          setOperator(operatorType);
          
          // Update lastSyncedFilterInfoRef to prevent re-processing
          lastSyncedFilterInfoRef.current = currentFilterInfoStr;
          
          // Set values based on operator type
          if (operatorType === 'between') {
            try {
              const parsed = JSON.parse(currentItem.value || '{}');
              if (parsed.start && parsed.end) {
                try {
                  setDateRange({
                    start: parseDate(parsed.start) as DateValue,
                    end: parseDate(parsed.end) as DateValue,
                  });
                  // Clear other value types when setting range
                  setDateValue(null);
                  setRelativePreset('');
                } catch (parseError) {
                  // If date parsing fails, clear the range
                  console.warn('Failed to parse date range:', parseError);
                  setDateRange(null);
                }
              } else {
                setDateRange(null);
              }
            } catch (jsonError) {
              // If JSON parsing fails, clear the range
              console.warn('Failed to parse date range JSON:', jsonError);
            setDateRange(null);
          }
        } else if (operatorType === 'relative_preset') {
          setRelativePreset(currentItem.value || '');
            // Clear other value types when setting preset
            setDateValue(null);
            setDateRange(null);
          } else if (operatorType === 'is_empty' || operatorType === 'is_not_empty') {
            // For is_empty and is_not_empty, value should be empty string
            setDateValue(null);
            setDateRange(null);
            setRelativePreset('');
          } else {
            // For other operators (equals, less, greater, etc.), use single date value
            if (currentItem.value) {
              try {
                const parsedDate = parseDate(currentItem.value);
                setDateValue(parsedDate);
                // Clear other value types when setting single date
                setDateRange(null);
                setRelativePreset('');
              } catch (parseError) {
                // If date parsing fails, clear the value
                console.warn('Failed to parse date value:', parseError);
                setDateValue(null);
              }
            } else {
              setDateValue(null);
            }
          }
        } else {
          // Filter info didn't change, but we should still sync values if needed
          // (this handles the case where only value changed, not operator)
          // Determine the operator type from currentItem (handle 'contains' -> 'equals' conversion)
          let operatorType = currentItem.type || 'equals';
          if (operatorType === 'contains') {
            operatorType = 'equals';
          }
          
          if (operatorType === 'between') {
            try {
              const parsed = JSON.parse(currentItem.value || '{}');
              if (parsed.start && parsed.end) {
                try {
                  setDateRange({
                    start: parseDate(parsed.start) as DateValue,
                    end: parseDate(parsed.end) as DateValue,
                  });
                } catch (parseError) {
                  console.warn('Failed to parse date range:', parseError);
                  setDateRange(null);
                }
              } else {
                setDateRange(null);
              }
            } catch (jsonError) {
              console.warn('Failed to parse date range JSON:', jsonError);
              setDateRange(null);
            }
          } else if (operatorType === 'relative_preset') {
            setRelativePreset(currentItem.value || '');
          } else if (operatorType === 'is_empty' || operatorType === 'is_not_empty') {
            setDateValue(null);
            setDateRange(null);
            setRelativePreset('');
          } else {
            if (currentItem.value) {
              try {
                const parsedDate = parseDate(currentItem.value);
                setDateValue(parsedDate);
              } catch (parseError) {
                console.warn('Failed to parse date value:', parseError);
                setDateValue(null);
              }
            } else {
              setDateValue(null);
            }
          }
        }
      } else {
        // No filter item, reset all values
        setDateValue(null);
        setDateRange(null);
        setRelativePreset('');
      }
    }, [filter.filterInfo]);

    const updateFilterInfo = React.useCallback(
      (op: string, val?: string, range?: { start: string; end: string }, preset?: string) => {
        let filterValue: string;
        if (op === 'is_empty' || op === 'is_not_empty') {
          filterValue = '';
        } else if (op === 'between') {
          filterValue = JSON.stringify(range || { start: '', end: '' });
        } else if (op === 'relative_preset') {
          filterValue = preset || '';
        } else {
          filterValue = val || '';
        }

        const updatedFilterInfo: IFilterItem[] = [
          {
            ...currentFilterItem,
            type: op,
            value: filterValue,
          },
        ];
        return notifyChange({
          ...filter,
          filterInfo: updatedFilterInfo,
        });
      },
      [filter, currentFilterItem, notifyChange]
    );

    const handleOperatorChange = React.useCallback(
      (newOperator: string) => {
        setOperator(newOperator);
        // For is_empty and is_not_empty operators, set value to empty string
        if (newOperator === 'is_empty' || newOperator === 'is_not_empty') {
          const didNotify = updateFilterInfo(newOperator, '', undefined, undefined);
          if (didNotify) {
            // Mark this as an internal update to prevent useEffect from resetting
            isInternalOperatorUpdateRef.current = true;
          }
        } else if (newOperator === 'between') {
          const normalizedRange =
            dateRange != null
              ? {
                  start: dateRange.start ? dateRange.start.toString() : '',
                  end: dateRange.end ? dateRange.end.toString() : '',
                }
              : { start: '', end: '' };
          const didNotify = updateFilterInfo(newOperator, undefined, normalizedRange, undefined);
          if (didNotify) {
            isInternalOperatorUpdateRef.current = true;
          }
        } else if (newOperator === 'relative_preset') {
          const didNotify = updateFilterInfo(newOperator, undefined, undefined, relativePreset);
          if (didNotify) {
            isInternalOperatorUpdateRef.current = true;
          }
        } else {
          const didNotify = updateFilterInfo(newOperator, dateValue?.toString() || '', undefined, undefined);
          if (didNotify) {
            isInternalOperatorUpdateRef.current = true;
          }
        }
      },
      [dateValue, dateRange, relativePreset, updateFilterInfo]
    );

    const handleDateChange = React.useCallback(
      (newDate: DateValue | null) => {
        setDateValue(newDate);
        updateFilterInfo(operator, newDate?.toString() || '', undefined, undefined);
      },
      [operator, updateFilterInfo]
    );

    const handleRangeChange = React.useCallback(
      (range: RangeValue<DateValue> | null) => {
        const hasCompleteRange = !!(range && range.start && range.end);
        setDateRange(hasCompleteRange ? range : null);
        const normalizedRange = hasCompleteRange
          ? {
              start: range!.start.toString(),
              end: range!.end.toString(),
            }
          : { start: '', end: '' };
        updateFilterInfo(operator, undefined, normalizedRange, undefined);
      },
      [operator, updateFilterInfo]
    );

    const handlePresetChange = React.useCallback(
      (preset: string) => {
        setRelativePreset(preset);
        updateFilterInfo(operator, undefined, undefined, preset);
      },
      [operator, updateFilterInfo]
    );

    return (
      <div ref={forwardedRef} className={styles.dateFilterControl}>
        <Flex direction="column" gap="size-200">
          {/* Operator row: Label + Selector */}
          <Flex 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between"
            gap="size-200"
            UNSAFE_className={styles.operatorRow}
          >
            <Text UNSAFE_className={styles.operatorLabel}>{filter.label || filter.name}</Text>
            <div className={styles.operatorSelectorWrapper}>
              <OperatorSelector
                operators={DATE_OPERATORS}
                selectedOperator={operator}
                onOperatorChange={handleOperatorChange}
                aria-label="Date filter operator"
              />
            </div>
          </Flex>
          
          {/* Date input row(s) */}
          {(operator !== 'is_empty' && operator !== 'is_not_empty') && (
            operator === 'between' ? (
              <div className={styles.dateRangeInput}>
                <DateRangePicker
                  value={dateRange}
                  onChange={handleRangeChange}
                  width="100%"
                  aria-label="Date range"
                />
              </div>
            ) : operator === 'relative_preset' ? (
              <div className={styles.presetInput}>
                <Picker
                  selectedKey={relativePreset}
                  onSelectionChange={(key) => handlePresetChange(key as string)}
                  width="100%"
                  aria-label="Date preset"
                >
                  {RELATIVE_PRESETS.map((preset) => (
                    <Item key={preset.id}>{preset.label}</Item>
                  ))}
                </Picker>
              </div>
            ) : (
              <div className={styles.dateInput}>
                <DatePicker
                  value={dateValue}
                  onChange={handleDateChange}
                  width="100%"
                  aria-label="Date value"
                />
              </div>
            )
          )}
        </Flex>
      </div>
    );
  }
);

DateFilterControl.displayName = 'DateFilterControl';

